import { AlertTriangle, FileText, Mic, PhoneIncoming } from 'lucide-react';
import { Modal, Badge, EmptyState } from '@/shared/ui';
import { Button } from '@/shared/ui/Button';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './CallDetailPanel.module.css';
import type { HandledOutcome, InboundCall, QueueStatus } from '../api/callsApi';
import { useCallQueueActions } from '../hooks/useCallLog';
import {
  getComplaint,
  getDisconnectionIssue,
  getDurationLabel,
  getOutcome,
  getPainLevel,
  getPatientName,
  getPostCallFields,
} from '../utils/callFields';

const OUTCOME_PILL: Record<string, string> = {
  success: patterns.pillSuccess,
  error: patterns.pillError,
  neutral: patterns.pillMuted ?? '',
};

// Complaint and pain level get their own dedicated section — drop them here to avoid showing twice.
const POST_CALL_GRID_EXCLUDE = new Set(['Reason for call', 'Pain level']);

interface TimelineEvent {
  label: string;
  timestamp: string;
  isError?: boolean;
}

// Derived from existing fields only — no persisted notification log.
function buildTimeline(call: InboundCall): TimelineEvent[] {
  const events: TimelineEvent[] = [{ label: 'Call received', timestamp: call.receivedAt }];

  if (call.priority !== 'normal') {
    events.push({ label: `Flagged ${call.priority}`, timestamp: call.receivedAt });
  }
  if (call.queueStatus) {
    events.push({ label: 'Added to callback queue', timestamp: call.receivedAt });
  }
  if (call.emailedAt) {
    events.push({ label: 'Summary emailed to front desk', timestamp: call.emailedAt });
  }
  if (call.notificationError) {
    // Failure is recorded after the alert claim, so that's the closest time we have.
    events.push({
      label: `${call.notificationError} — the front desk was not notified by email`,
      timestamp: call.alertsSentAt ?? call.receivedAt,
      isError: true,
    });
  }
  if (call.handledOutcome) {
    events.push({
      label: `Marked ${call.handledOutcome.replace('_', ' ')}`,
      timestamp: call.handledAt ?? call.updatedAt,
    });
  }

  return events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface CallDetailPanelProps {
  call: InboundCall | null;
  onClose: () => void;
}

export function CallDetailPanel({ call, onClose }: CallDetailPanelProps) {
  const { updateQueue } = useCallQueueActions();

  if (!call) return null;

  const patientName = getPatientName(call);
  const complaint = getComplaint(call);
  const painLevel = getPainLevel(call);
  const postCallFields = getPostCallFields(call).filter(
    (f) => !POST_CALL_GRID_EXCLUDE.has(f.label),
  );
  const timeline = buildTimeline(call);
  const outcome = getOutcome(call);
  const disconnectIssue = getDisconnectionIssue(call);
  const duration = getDurationLabel(call);
  const isOpenQueueItem = !!call.queueStatus && call.queueStatus !== 'handled';
  const isPending = updateQueue.isPending;
  // Which button was clicked — so only it spins, while all are disabled.
  const pendingKey = isPending
    ? (updateQueue.variables?.handledOutcome ?? updateQueue.variables?.queueStatus ?? null)
    : null;

  const markHandled = (handledOutcome: HandledOutcome) =>
    updateQueue.mutate({ id: call.id, handledOutcome });
  const setQueueStatus = (queueStatus: QueueStatus) =>
    updateQueue.mutate({ id: call.id, queueStatus });
  const reopen = () =>
    updateQueue.mutate({ id: call.id, queueStatus: 'new', handledOutcome: null });

  const queueButton = (key: string, label: string, onClick: () => void) => (
    <Button
      variant="secondary"
      size="sm"
      loading={pendingKey === key}
      disabled={isPending}
      onClick={onClick}
    >
      {label}
    </Button>
  );

  return (
    <Modal
      isOpen={!!call}
      onClose={onClose}
      variant="side"
      size="lg"
      title={patientName ?? call.callerNumber ?? 'Call detail'}
      icon={<PhoneIncoming size={16} aria-hidden />}
      footer={
        isOpenQueueItem ? (
          <div className={styles.queueActions}>
            <span className={styles.queueActionsLabel}>Callback:</span>
            {call.queueStatus !== 'in_progress' &&
              queueButton('in_progress', 'Calling now', () => setQueueStatus('in_progress'))}
            {call.queueStatus !== 'no_answer' &&
              queueButton('no_answer', 'No answer — retry', () => setQueueStatus('no_answer'))}
            <span className={styles.queueActionsLabel}>Mark handled:</span>
            {queueButton('booked', 'Booked', () => markHandled('booked'))}
            {queueButton('not_booked', 'Not booked', () => markHandled('not_booked'))}
            {queueButton('not_reached', 'Not reached', () => markHandled('not_reached'))}
          </div>
        ) : call.queueStatus === 'handled' ? (
          <div className={styles.queueActions}>
            <span className={styles.queueActionsLabel}>
              Handled{call.handledOutcome ? ` — ${call.handledOutcome.replace('_', ' ')}` : ''}
            </span>
            {queueButton('new', 'Reopen callback', reopen)}
          </div>
        ) : undefined
      }
    >
      <div className={styles.section}>
        <div className={styles.headerRow}>
          <span className={styles.headerMuted}>{formatDateTime(call.receivedAt)}</span>
          {call.priority !== 'normal' && (
            <span
              className={call.priority === 'urgent' ? patterns.pillError : patterns.pillWarning}
            >
              {call.priority === 'urgent' ? 'Urgent' : 'High'}
            </span>
          )}
          {outcome && <span className={OUTCOME_PILL[outcome.kind] ?? ''}>{outcome.label}</span>}
        </div>
        <div className={styles.headerMeta}>
          {[
            call.agentName ?? 'Deleted agent',
            call.source && call.source !== call.agentName ? call.source : null,
            call.callerNumber ? `from ${call.callerNumber}` : null,
            duration,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {disconnectIssue && (
          <div className={styles.issueRow}>
            <AlertTriangle size={12} aria-hidden /> How the call ended on Retell: {disconnectIssue}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Notification timeline</h3>
        <ul className={styles.timeline}>
          {timeline.map((event, i) => (
            <li key={`${event.label}-${i}`} className={styles.timelineItem}>
              <span
                className={`${styles.timelineDot} ${event.isError ? styles.timelineDotError : ''}`}
                aria-hidden
              />
              <span
                className={`${styles.timelineLabel} ${event.isError ? styles.timelineLabelError : ''}`}
              >
                {event.label}
              </span>
              <span className={styles.timelineTime}>{formatDateTime(event.timestamp)}</span>
            </li>
          ))}
        </ul>
      </div>

      {call.summaryJson.summary && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Summary</h3>
          <p className={styles.complaint}>{call.summaryJson.summary}</p>
        </div>
      )}

      {(complaint || painLevel) && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Complaint & pain level</h3>
          {complaint && <p className={styles.complaint}>{complaint}</p>}
          {painLevel && <Badge variant="warning">Pain level: {painLevel}</Badge>}
        </div>
      )}

      {call.summaryJson.action && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Message for doctor</h3>
          <p className={styles.complaint}>{call.summaryJson.action}</p>
        </div>
      )}

      {postCallFields.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Post-call details</h3>
          <div className={styles.fieldGrid}>
            {postCallFields.map((f) => (
              <FieldRow key={f.label} label={f.label} value={f.value} />
            ))}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Mic size={13} aria-hidden /> Recording
        </h3>
        {call.recordingUrl ? (
          <audio controls src={call.recordingUrl} className={styles.audioPlayer} />
        ) : (
          <EmptyState compact title="No recording captured for this call" />
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FileText size={13} aria-hidden /> Transcript
        </h3>
        {call.transcript ? (
          <pre className={styles.transcript}>{call.transcript}</pre>
        ) : (
          <EmptyState compact title="No transcript available for this call" />
        )}
      </div>

      <p className={styles.callId}>Retell call ID: {call.retellCallId}</p>
    </Modal>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  );
}
